import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react'
import { useAuth } from './AuthContext'
import { currencyAPI } from '../lib/api'
import {
  CURRENCY_STORAGE_KEY,
  DEFAULT_CAD_TO_RATES,
  SUPPORTED_CURRENCIES,
  convertCadAmount,
  convertToCadAmount,
  formatCurrencyAmount,
  inferCurrencyFromCountry,
  isSupportedCurrency,
  normalizeCurrency,
} from '../lib/pricing'

const CurrencyContext = createContext(null)

export const useCurrency = () => {
  const context = useContext(CurrencyContext)
  if (!context) {
    throw new Error('useCurrency must be used within CurrencyProvider')
  }
  return context
}

const normalizeRatesPayload = (payload) => {
  const sourceRates = payload?.rates || {}
  const merged = { ...DEFAULT_CAD_TO_RATES }
  Object.keys(sourceRates).forEach((key) => {
    const normalized = normalizeCurrency(key)
    const value = Number(sourceRates[key])
    if (!Number.isFinite(value) || value <= 0) return
    merged[normalized] = value
  })
  merged.CAD = 1
  return merged
}

export const CurrencyProvider = ({ children }) => {
  const { user, profile, preferredCurrency, setPreferredCurrency } = useAuth()
  const [currency, setCurrencyState] = useState('CAD')
  const [rates, setRates] = useState({ ...DEFAULT_CAD_TO_RATES })
  const [ratesMeta, setRatesMeta] = useState({ stale: true, unavailable: [], fetchedAt: null })
  const [ratesLoading, setRatesLoading] = useState(false)

  const loadRates = useCallback(async () => {
    setRatesLoading(true)
    try {
      const result = await currencyAPI.getRates()
      if (result?.success && result?.data) {
        const normalizedRates = normalizeRatesPayload(result.data)
        setRates(normalizedRates)
        setRatesMeta({
          stale: Boolean(result.data.stale),
          unavailable: Array.isArray(result.data.unavailable) ? result.data.unavailable : [],
          fetchedAt: result.data.fetched_at || null,
        })
      }
    } catch (_error) {
      // Keep defaults.
    } finally {
      setRatesLoading(false)
    }
  }, [])

  useEffect(() => {
    loadRates()
    const timer = setInterval(loadRates, 60 * 60 * 1000)
    return () => clearInterval(timer)
  }, [loadRates])

  useEffect(() => {
    if (user) {
      const fromAuth = normalizeCurrency(preferredCurrency || '')
      if (isSupportedCurrency(fromAuth)) {
        setCurrencyState(fromAuth)
        return
      }

      const inferredFromProfile = inferCurrencyFromCountry(profile?.country_code)
      setCurrencyState(inferredFromProfile)
      return
    }

    const saved = normalizeCurrency(localStorage.getItem(CURRENCY_STORAGE_KEY) || '')
    if (isSupportedCurrency(saved)) {
      setCurrencyState(saved)
      return
    }

    setCurrencyState('CAD')
  }, [user, profile?.country_code, preferredCurrency])

  const setCurrency = useCallback(async (nextCurrency, options = { persist: true }) => {
    const normalized = normalizeCurrency(nextCurrency)
    if (!isSupportedCurrency(normalized)) return false
    const previousCurrency = currency

    setCurrencyState(normalized)

    if (!options?.persist) {
      return true
    }

    if (user) {
      const result = await currencyAPI.updatePreference(normalized)
      if (result?.success) {
        setPreferredCurrency(normalized)
        return true
      }
      // Keep UI + persisted preference consistent.
      setCurrencyState(previousCurrency)
      return false
    }

    localStorage.setItem(CURRENCY_STORAGE_KEY, normalized)
    return true
  }, [currency, setPreferredCurrency, user])

  const convertCad = useCallback((amountCad, targetCurrency = currency) => {
    return convertCadAmount(amountCad, targetCurrency, rates)
  }, [currency, rates])

  const convertToCad = useCallback((amountInCurrency, sourceCurrency = currency) => {
    return convertToCadAmount(amountInCurrency, sourceCurrency, rates)
  }, [currency, rates])

  const formatAmount = useCallback((amount, currencyCode = currency, options = {}) => {
    return formatCurrencyAmount(amount, currencyCode, options)
  }, [currency])

  const value = useMemo(() => ({
    currency,
    supportedCurrencies: SUPPORTED_CURRENCIES,
    rates,
    ratesMeta,
    ratesLoading,
    setCurrency,
    convertCadAmount: convertCad,
    convertToCadAmount: convertToCad,
    formatCurrencyAmount: formatAmount,
  }), [convertCad, convertToCad, currency, formatAmount, rates, ratesLoading, ratesMeta, setCurrency])

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  )
}
